// frontend/src/services/activityService.ts
// Tracks user file interactions (play, view, download) in localStorage.

import { getDisplayFileName } from './FileUtils';
import { FileType } from './api';

export type ActivityAction = 'played' | 'viewed' | 'downloaded' | 'searched';

export interface ActivityEntry {
  id: string;           // unique event id
  key: string;          // S3 object key (or search query for 'searched')
  displayName: string;  // human-readable name
  action: ActivityAction;
  fileType: FileType | null;
  timestamp: string;    // ISO 8601
}

const STORAGE_KEY = 'openbay_activity';
const MAX_ENTRIES = 50;

function load(): ActivityEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as ActivityEntry[];
  } catch {
    return [];
  }
}

function save(entries: ActivityEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // localStorage may be unavailable (private browsing quota, etc.)
  }
}

/**
 * Record a file interaction. Deduplicates by key+action within the same minute
 * so rapid re-plays don't flood the list.
 */
export function recordActivity(
  key: string,
  action: ActivityAction,
  fileType: FileType | null = null,
  displayName?: string
): void {
  const entries = load();
  const now = new Date();
  const minuteBucket = now.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM"

  // Deduplicate: remove any existing entry for the same key+action in the same minute
  const deduped = entries.filter(
    (e) => !(e.key === key && e.action === action && e.timestamp.startsWith(minuteBucket))
  );

  const entry: ActivityEntry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    key,
    displayName: displayName ?? (action === 'searched' ? key : getDisplayFileName(key)),
    action,
    fileType,
    timestamp: now.toISOString(),
  };

  // Prepend newest first, cap at MAX_ENTRIES
  const updated = [entry, ...deduped].slice(0, MAX_ENTRIES);
  save(updated);
}

/** Return all activity entries, newest first. */
export function getActivity(): ActivityEntry[] {
  return load();
}

/** Remove a single entry by id. */
export function removeActivity(id: string): void {
  const entries = load().filter((e) => e.id !== id);
  save(entries);
}

/** Wipe all activity. */
export function clearActivity(): void {
  localStorage.removeItem(STORAGE_KEY);
}
